const VIRTUAL_TOTAL_SIZE_NOISE_THRESHOLD_PX = 2
const VIRTUAL_START_NOISE_THRESHOLD_PX = 2
const VIRTUAL_VIEWPORT_RESIDUAL_THRESHOLD_PX = 4

export function shouldAdjustVirtualScrollForMeasuredSizeChange({
    itemStart,
    itemEnd = null,
    delta,
    scrollOffset,
    scrollAdjustment = 0,
    scrollDirection = null,
    hasMeasuredSize = false,
    allowMeasuredShrinkAdjustment = true,
}: {
    itemStart: number
    itemEnd?: number | null
    delta: number
    scrollOffset: number
    scrollAdjustment?: number
    scrollDirection?: 'forward' | 'backward' | null
    hasMeasuredSize?: boolean
    allowMeasuredShrinkAdjustment?: boolean
}) {
    if (
        !Number.isFinite(itemStart) ||
        !Number.isFinite(delta) ||
        !Number.isFinite(scrollOffset) ||
        !Number.isFinite(scrollAdjustment)
    ) {
        return false
    }

    if (delta === 0) return false

    const adjustedScrollOffset = scrollOffset + scrollAdjustment
    const measuredItemEnd = itemEnd == null ? Number.NaN : Number(itemEnd)
    const measuredItemFullyAboveViewport =
        Number.isFinite(measuredItemEnd) && measuredItemEnd <= adjustedScrollOffset
    if (delta < 0 && !allowMeasuredShrinkAdjustment) return false
    if (scrollDirection === 'backward' && hasMeasuredSize) {
        return measuredItemFullyAboveViewport
    }
    if (delta < 0) return measuredItemFullyAboveViewport

    return itemStart < adjustedScrollOffset
}

export function resolveRetainedVirtualTotalSize({
    totalSize,
    retainedTotalSize,
    scrollTop,
    scrollHeight,
    clientHeight,
    isScrolling = false,
    retainGrowth = false,
}: {
    totalSize: number
    retainedTotalSize: number
    scrollTop?: number | null
    scrollHeight?: number | null
    clientHeight?: number | null
    isScrolling?: boolean
    retainGrowth?: boolean
}) {
    const nextTotalSize = Math.max(0, Number(totalSize) || 0)
    const previousTotalSize = Math.max(0, Number(retainedTotalSize) || 0)

    if (previousTotalSize <= 0) {
        return nextTotalSize
    }

    if (nextTotalSize > previousTotalSize) {
        return retainGrowth ? previousTotalSize : nextTotalSize
    }

    if (nextTotalSize === previousTotalSize) {
        return nextTotalSize
    }

    if (previousTotalSize - nextTotalSize <= VIRTUAL_TOTAL_SIZE_NOISE_THRESHOLD_PX) {
        return previousTotalSize
    }

    const currentScrollTop = Number(scrollTop)
    const currentScrollHeight = Number(scrollHeight)
    const currentClientHeight = Number(clientHeight)
    if (
        !Number.isFinite(currentScrollTop) ||
        !Number.isFinite(currentScrollHeight) ||
        !Number.isFinite(currentClientHeight) ||
        currentClientHeight <= 0
    ) {
        return nextTotalSize
    }

    // Retain shrink only while the user is actively scrolling. Once the list is
    // idle, releasing the corrected total prevents stale spacer height from
    // becoming blank scrollable space at the end of a virtual list.
    if (!isScrolling) return nextTotalSize

    return previousTotalSize
}

export function resolveVirtualItemStartCorrection({
    item,
    retainedStarts = new Map(),
    retainScrollMovement = false,
    retainUpwardMovement = false,
}: {
    item?: { key: unknown; start: number } | null
    retainedStarts?: Map<string, number>
    retainScrollMovement?: boolean
    retainUpwardMovement?: boolean
}) {
    if (!item) return 0
    const key = String(item.key)
    if (!retainedStarts.has(key)) return 0

    const previousStart = Number(retainedStarts.get(key))
    const currentStart = Number(item.start)
    if (!Number.isFinite(previousStart) || !Number.isFinite(currentStart)) return 0

    const correction = previousStart - currentStart
    if (correction === 0 || !Number.isFinite(correction)) return 0
    if (!retainScrollMovement && Math.abs(correction) > VIRTUAL_START_NOISE_THRESHOLD_PX) {
        return retainUpwardMovement && correction > 0 ? correction : 0
    }

    return correction
}

export function resolveVirtualItemViewportCorrection({
    item,
    retainedStarts = new Map(),
    retainedScrollTop,
    scrollTop,
}: {
    item?: { key: unknown; start: number } | null
    retainedStarts?: Map<string, number>
    retainedScrollTop?: number | null
    scrollTop?: number | null
}) {
    if (!item) return 0
    const key = String(item.key)
    if (!retainedStarts.has(key)) return 0

    const previousStart = Number(retainedStarts.get(key))
    const currentStart = Number(item.start)
    const previousScrollTop = Number(retainedScrollTop)
    const currentScrollTop = Number(scrollTop)
    if (
        !Number.isFinite(previousStart) ||
        !Number.isFinite(currentStart) ||
        !Number.isFinite(previousScrollTop) ||
        !Number.isFinite(currentScrollTop)
    ) {
        return 0
    }

    const correction = previousStart - previousScrollTop - (currentStart - currentScrollTop)
    if (correction === 0 || !Number.isFinite(correction)) return 0
    if (Math.abs(correction) > VIRTUAL_VIEWPORT_RESIDUAL_THRESHOLD_PX) return 0

    return correction
}

export function resolveVirtualStartCorrection({
    items = [],
    retainedStarts = new Map(),
    retainScrollMovement = false,
    retainUpwardMovement = false,
}: {
    items?: Array<{ key: unknown; start: number }>
    retainedStarts?: Map<string, number>
    retainScrollMovement?: boolean
    retainUpwardMovement?: boolean
}) {
    for (const item of items) {
        const correction = resolveVirtualItemStartCorrection({
            item,
            retainedStarts,
            retainScrollMovement,
            retainUpwardMovement,
        })
        if (correction !== 0) return correction
    }

    return 0
}

export function shouldRetainVirtualStartCorrectionsForTotalSize({
    totalSize,
    previousTotalSize,
    retainOnTotalSizeChange = false,
    retainOnBackwardTotalSizeChange = false,
    scrollDirection = null,
    hasVirtualizerScrollAdjustment = false,
}: {
    totalSize: number
    previousTotalSize?: number | null
    retainOnTotalSizeChange?: boolean
    retainOnBackwardTotalSizeChange?: boolean
    scrollDirection?: 'forward' | 'backward' | null
    hasVirtualizerScrollAdjustment?: boolean
}) {
    const nextTotalSize = Number(totalSize)
    const priorTotalSize = Number(previousTotalSize)
    if (!Number.isFinite(nextTotalSize)) return false
    if (!Number.isFinite(priorTotalSize) || priorTotalSize <= 0) return true

    if (Math.abs(nextTotalSize - priorTotalSize) <= VIRTUAL_TOTAL_SIZE_NOISE_THRESHOLD_PX) {
        return true
    }

    if (hasVirtualizerScrollAdjustment) return false
    if (retainOnTotalSizeChange) return true

    return retainOnBackwardTotalSizeChange && scrollDirection === 'backward'
}

export function prioritizeVirtualItemsForViewport({
    items = [],
    scrollTop,
    clientHeight,
}: {
    items?: Array<{ key: unknown; start: number; end: number }>
    scrollTop?: number | null
    clientHeight?: number | null
}) {
    const currentScrollTop = Number(scrollTop)
    const currentClientHeight = Number(clientHeight)
    if (
        items.length === 0 ||
        !Number.isFinite(currentScrollTop) ||
        !Number.isFinite(currentClientHeight) ||
        currentClientHeight <= 0
    ) {
        return items
    }

    const viewportBottom = currentScrollTop + currentClientHeight
    const viewportCenter = currentScrollTop + currentClientHeight / 2
    const visible: typeof items = []
    const rest: typeof items = []

    items.forEach(item => {
        const start = Number(item.start)
        const end = Number(item.end)
        if (
            Number.isFinite(start) &&
            Number.isFinite(end) &&
            end >= currentScrollTop &&
            start <= viewportBottom
        ) {
            visible.push(item)
        } else {
            rest.push(item)
        }
    })

    visible.sort((left, right) => {
        const leftCenter = (Number(left.start) + Number(left.end)) / 2
        const rightCenter = (Number(right.start) + Number(right.end)) / 2
        return Math.abs(leftCenter - viewportCenter) - Math.abs(rightCenter - viewportCenter)
    })

    return visible.length > 0 ? [...visible, ...rest] : items
}

type VirtualScrollElementLike = {
    scrollTop?: number
    getBoundingClientRect?: () => { top?: number } | null
}

export function resolveVirtualScrollMargin({
    scrollElement,
    listElement,
}: {
    scrollElement?: VirtualScrollElementLike | null
    listElement?: VirtualScrollElementLike | null
}) {
    if (!scrollElement || !listElement) return null

    const scrollRect = scrollElement.getBoundingClientRect?.()
    const listRect = listElement.getBoundingClientRect?.()
    const scrollTop = Number(scrollElement.scrollTop || 0)
    const scrollRectTop = Number(scrollRect?.top)
    const listRectTop = Number(listRect?.top)

    if (
        !Number.isFinite(scrollTop) ||
        !Number.isFinite(scrollRectTop) ||
        !Number.isFinite(listRectTop)
    ) {
        return null
    }

    return Math.max(0, Math.round(listRectTop - scrollRectTop + scrollTop))
}

export function shouldUpdateVirtualScrollMargin({
    currentMargin,
    nextMargin,
    threshold = 2,
}: {
    currentMargin: number
    nextMargin: number | null
    threshold?: number
}) {
    if (nextMargin == null) return false
    if (!Number.isFinite(currentMargin) || !Number.isFinite(nextMargin)) return false
    return Math.abs(currentMargin - Number(nextMargin)) > threshold
}

export function virtualItemRangeKey(items: Array<{ key: unknown }> = []) {
    return items.map(item => String(item.key)).join('|')
}
